import api from '@/lib/axios';

function unwrap<T>(r: { data: { data?: T } | T }): T {
  const body: any = r.data;
  return (body?.data ?? body) as T;
}

export type ClassChangeRequestStatus = 'pending' | 'approved' | 'rejected' | 'partial_failure';

export interface ClassChangeRequest {
  id: string;
  studentId: string;
  studentName: string | null;
  admissionNo: string | null;
  currentSchoolId: string;
  currentSectionId: string;
  currentClassName: string | null;
  currentSectionName: string | null;
  newSchoolId: string;
  newSectionId: string;
  newClassName: string | null;
  newSectionName: string | null;
  reason: string | null;
  status: ClassChangeRequestStatus;
  requestedBy: string | null;
  requestedByName: string | null;
  remarks: string | null;
  approvedBy: string | null;
  approvedByName: string | null;
  approvedAt: string | null;
  rejectedBy: string | null;
  rejectedByName: string | null;
  rejectedAt: string | null;
  partialFailureNote: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TargetSchool {
  id: string;
  name: string;
}

export interface TargetSection {
  id: string;
  name: string;
}

export interface TargetClass {
  id: string;
  name: string;
  sections: TargetSection[];
}

export interface CreateClassChangeRequestPayload {
  studentId: string;
  newSchoolId: string;
  newSectionId: string;
  reason?: string;
}

export interface UpdateClassChangeRequestPayload {
  newSchoolId?: string;
  newSectionId?: string;
  reason?: string;
}

/** School tier — create/edit/withdraw the requesting school's own requests. */
export const SchoolClassChangeRequestsApi = {
  list: async (): Promise<ClassChangeRequest[]> => unwrap(await api.get('/school/class-change-requests')),

  targetSchools: async (): Promise<TargetSchool[]> => unwrap(await api.get('/school/class-change-requests/target-schools')),

  targetClasses: async (schoolId: string): Promise<TargetClass[]> =>
    unwrap(await api.get(`/school/class-change-requests/target-schools/${schoolId}/classes`)),

  create: async (payload: CreateClassChangeRequestPayload): Promise<ClassChangeRequest> =>
    unwrap(await api.post('/school/class-change-requests', payload)),

  update: async (id: string, payload: UpdateClassChangeRequestPayload): Promise<ClassChangeRequest> =>
    unwrap(await api.patch(`/school/class-change-requests/${id}`, payload)),

  withdraw: async (id: string) => unwrap(await api.delete(`/school/class-change-requests/${id}`)),
};

export interface ListOrgClassChangeRequestsParams {
  status?: ClassChangeRequestStatus;
  schoolId?: string;
}

export interface OrgSchoolOption {
  id: string;
  name: string;
}

export interface OrgStudentOption {
  id: string;
  admissionNo: string;
  name: string | null;
}

export interface CreateOrgClassChangeRequestPayload {
  currentSchoolId: string;
  studentId: string;
  newSchoolId: string;
  newSectionId: string;
  reason?: string;
}

/**
 * Org tier — create (on behalf of any school in scope), global view, and
 * approve/reject, all from one console page — matching the legacy
 * single-page layout, isolated to the caller's own organization on both
 * sides of every request.
 */
export const OrgClassChangeRequestsApi = {
  list: async (params?: ListOrgClassChangeRequestsParams): Promise<ClassChangeRequest[]> =>
    unwrap(await api.get('/org/class-change-requests', { params })),

  schools: async (): Promise<OrgSchoolOption[]> => unwrap(await api.get('/org/class-change-requests/schools')),

  classesForSchool: async (schoolId: string): Promise<TargetClass[]> =>
    unwrap(await api.get(`/org/class-change-requests/schools/${schoolId}/classes`)),

  studentsForSection: async (schoolId: string, sectionId: string): Promise<OrgStudentOption[]> =>
    unwrap(await api.get(`/org/class-change-requests/schools/${schoolId}/sections/${sectionId}/students`)),

  create: async (payload: CreateOrgClassChangeRequestPayload): Promise<ClassChangeRequest> =>
    unwrap(await api.post('/org/class-change-requests', payload)),

  approve: async (id: string, remarks?: string): Promise<ClassChangeRequest> =>
    unwrap(await api.patch(`/org/class-change-requests/${id}/approve`, { remarks })),

  reject: async (id: string, remarks?: string): Promise<ClassChangeRequest> =>
    unwrap(await api.patch(`/org/class-change-requests/${id}/reject`, { remarks })),
};
